// Bills (M4 plan D1 to D3, D10): receipts in, checked by a person. Upload from the computer or the
// phone camera, or forward emails to the business's private address.
import { type FormEvent, useState } from 'react';
import { useOrgData } from '../accountant/useOrgData.js';
import type { Api } from '../api.js';
import { EmptyState } from '../EmptyState.js';
import { day, dollars, fromCents } from '../format.js';

interface Receipt {
  id: string;
  source: 'upload' | 'email';
  status: 'reading' | 'ready' | 'confirmed' | 'failed' | 'not_receipt';
  file_name: string | null;
  email_from: string | null;
  email_subject: string | null;
  supplier: string | null;
  abn: string | null;
  invoice_number: string | null;
  date: string | null;
  currency: string | null;
  total_minor: string | null;
  gst_minor: string | null;
  paid: boolean;
  due_date: string | null;
  notes: string[] | null;
  bank_line_id: string | null;
}

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/gif';

export function Bills({ api, orgId }: { api: Api; orgId: string }) {
  const list = useOrgData<{ receipts: Receipt[]; address: string | null }>(api, orgId, '/receipts');
  const [showOthers, setShowOthers] = useState(false);
  if (list.error) return <p className="error">{list.error}</p>;
  if (!list.data) return <p className="muted">Loading</p>;
  const { receipts, address } = list.data;
  const toCheck = receipts.filter(
    (r) => r.status === 'ready' || r.status === 'failed' || r.status === 'reading',
  );
  const done = receipts.filter((r) => r.status === 'confirmed');
  const others = receipts.filter((r) => r.status === 'not_receipt');

  return (
    <section className="stack">
      <h1>Bills</h1>
      {address && <ForwardingAddress address={address} />}
      <UploadReceipts api={api} orgId={orgId} onUploaded={list.reload} />
      {toCheck.length > 0 && (
        <>
          <h2>To check</h2>
          {toCheck.map((r) => (
            <ReceiptCheck key={r.id} api={api} orgId={orgId} receipt={r} onDone={list.reload} />
          ))}
        </>
      )}
      {done.length > 0 && (
        <table className="table">
          <caption>Checked</caption>
          <tbody>
            {done.map((r) => (
              <tr key={r.id}>
                <td>{r.date ? day(r.date) : ''}</td>
                <td>{r.supplier}</td>
                <td className="num">{amount(r.total_minor, r.currency)}</td>
                <td className="num muted">
                  {r.gst_minor ? `GST ${amount(r.gst_minor, 'AUD')}` : 'No GST'}
                </td>
                <td>
                  {r.bank_line_id
                    ? 'Matched to the bank'
                    : r.paid
                      ? 'Waiting for the bank line'
                      : 'Waiting for payment'}
                </td>
                <td>
                  <ViewFile api={api} orgId={orgId} id={r.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {others.length > 0 && (
        <div className="stack">
          <button type="button" className="link" onClick={() => setShowOthers((v) => !v)}>
            {showOthers ? 'Hide' : `${others.length} emails that were not receipts`}
          </button>
          {showOthers && (
            <table className="table">
              <tbody>
                {others.map((r) => (
                  <tr key={r.id}>
                    <td>{r.email_from}</td>
                    <td>{r.email_subject}</td>
                    <td>
                      <ViewFile api={api} orgId={orgId} id={r.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {receipts.length === 0 && (
        <EmptyState art="receipts">
          <h2>No receipts yet</h2>
          <p className="muted">
            Forward a receipt to your Foderis address or upload a photo. Foderis reads the supplier,
            total and GST for you.
          </p>
        </EmptyState>
      )}
    </section>
  );
}

function amount(minor: string | null, currency: string | null): string {
  if (minor === null) return '';
  const value = dollars(fromCents(minor));
  return currency === 'AUD' || !currency ? value : `${currency} ${value.replace('$', '')}`;
}

function ForwardingAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="stack bank-card">
      <p>
        Forward receipts to <strong>{address}</strong>{' '}
        <button type="button" className="link" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </p>
      <p className="muted">
        In Gmail, add it under Settings, Forwarding. Gmail sends a confirmation email to this
        address first: it appears below under "emails that were not receipts", where you can open it
        and copy the code. Then create a filter for your receipts and choose "Forward it to" this
        address.
      </p>
    </div>
  );
}

function UploadReceipts({
  api,
  orgId,
  onUploaded,
}: {
  api: Api;
  orgId: string;
  onUploaded: () => void;
}) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError('');
    try {
      let n = 0;
      for (const file of Array.from(files)) {
        n++;
        setBusy(`Reading ${n} of ${files.length}`);
        const contentType = file.type || 'application/pdf';
        const ticket = await api.send<{
          key: string;
          url: string;
          headers: Record<string, string>;
        }>(orgId, 'POST', '/uploads', { kind: 'receipt', contentType });
        const res = await fetch(ticket.url, { method: 'PUT', headers: ticket.headers, body: file });
        if (!res.ok) throw new Error(`${file.name} could not be uploaded. Try again.`);
        await api.send(orgId, 'POST', '/receipts', {
          key: ticket.key,
          contentType,
          fileName: file.name,
        });
      }
      onUploaded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The receipt could not be uploaded.');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="stack">
      <label className="button-like phone-only">
        {busy || 'Take a photo of a receipt'}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          disabled={!!busy}
          onChange={(e) => {
            void upload(e.target.files);
            e.target.value = '';
          }}
        />
      </label>
      <label className="button-like">
        {busy || 'Add receipts (PDF or photo)'}
        <input
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          disabled={!!busy}
          onChange={(e) => {
            void upload(e.target.files);
            e.target.value = '';
          }}
        />
      </label>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function ViewFile({ api, orgId, id }: { api: Api; orgId: string; id: string }) {
  async function open() {
    // Open the tab first: browsers block windows opened after an await.
    const tab = window.open('', '_blank');
    try {
      const { url } = await api.get<{ url: string }>(orgId, `/receipts/${id}/file`);
      if (tab) tab.location.href = url;
    } catch {
      tab?.close();
    }
  }
  return (
    <button type="button" className="link" onClick={open}>
      View
    </button>
  );
}

function ReceiptCheck(props: { api: Api; orgId: string; receipt: Receipt; onDone: () => void }) {
  const { api, orgId, receipt: r } = props;
  const [supplier, setSupplier] = useState(r.supplier ?? '');
  const [date, setDate] = useState(r.date ?? '');
  const [currency, setCurrency] = useState(r.currency ?? 'AUD');
  const [total, setTotal] = useState(r.total_minor ? fromCents(r.total_minor) : '');
  const [gst, setGst] = useState(r.gst_minor ? fromCents(r.gst_minor) : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function confirm(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.send(orgId, 'POST', `/receipts/${r.id}/confirm`, {
        supplier,
        date,
        currency,
        total,
        gst: currency === 'AUD' && gst.trim() ? gst : null,
      });
      props.onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the receipt.');
    } finally {
      setBusy(false);
    }
  }

  if (r.status === 'reading') return <p className="muted">Reading {r.file_name ?? 'a receipt'}</p>;
  return (
    <form onSubmit={confirm} className="stack todo">
      <div className="title-row">
        <strong>{r.email_subject ?? r.file_name ?? 'Receipt'}</strong>
        <ViewFile api={api} orgId={orgId} id={r.id} />
      </div>
      {(r.notes ?? []).map((n) => (
        <p key={n} className="muted">
          {n}
        </p>
      ))}
      <div className="grid-2">
        <label>
          Supplier
          <input value={supplier} onChange={(e) => setSupplier(e.target.value)} required />
        </label>
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
        <label>
          Total
          <span className="inline">
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {['AUD', 'USD', 'EUR', 'GBP', 'NZD', 'SGD', 'JPY', 'CAD'].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <input
              inputMode="decimal"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              required
            />
          </span>
        </label>
        {currency === 'AUD' && (
          <label>
            GST on the receipt (leave empty if none)
            <input inputMode="decimal" value={gst} onChange={(e) => setGst(e.target.value)} />
          </label>
        )}
      </div>
      {!r.paid && r.due_date && <p className="muted">Due {day(r.due_date)}.</p>}
      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy}>
        Looks right
      </button>
    </form>
  );
}
